/**
 * Lists don't reorder under the cursor (docs/ux/review/interaction-visual.md #10, dev review #12):
 * while the pointer or keyboard focus is in a list, rows keep their place (and their group) and
 * only their content updates; new rows are appended to their group, gone rows disappear. The new
 * order applies once the user pauses (IDLE_MS without pointer or key activity) or leaves the list.
 */
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react'

export const IDLE_MS = 2500

export interface StableShape<G, E> {
  groupKey: (g: G) => string
  entries: (g: G) => readonly E[]
  entryKey: (e: E) => string
  withEntries: (g: G, entries: E[]) => G
}

/** `next`'s content in `prev`'s order. Pure. */
export function stabilize<G, E>(
  prev: readonly G[] | undefined,
  next: readonly G[],
  s: StableShape<G, E>,
): G[] {
  if (!prev?.length) return [...next]
  const where = new Map<string, { e: E; g: string }>()
  const nextGroups = new Map<string, G>()
  for (const g of next) {
    nextGroups.set(s.groupKey(g), g)
    for (const e of s.entries(g)) where.set(s.entryKey(e), { e, g: s.groupKey(g) })
  }
  const placed = new Set<string>()
  const out = new Map<string, { base: G; list: E[] }>()
  for (const g of prev) {
    const k = s.groupKey(g)
    const list: E[] = []
    for (const old of s.entries(g)) {
      const hit = where.get(s.entryKey(old))
      if (!hit) continue
      placed.add(s.entryKey(old))
      list.push(hit.e)
    }
    out.set(k, { base: nextGroups.get(k) ?? g, list })
  }
  for (const g of next) {
    const k = s.groupKey(g)
    let slot = out.get(k)
    if (!slot) {
      slot = { base: g, list: [] }
      out.set(k, slot)
    }
    for (const e of s.entries(g)) if (!placed.has(s.entryKey(e))) slot.list.push(e)
  }
  return [...out.values()].filter((x) => x.list.length).map((x) => s.withEntries(x.base, x.list))
}

/** True while the pointer or focus is inside `root` and the user was active within IDLE_MS. */
export function useInteracting(root: RefObject<HTMLElement | null>, idleMs = IDLE_MS): boolean {
  const [on, setOn] = useState(false)
  const inside = useRef({ hover: false, focus: false })
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => {
    const el = root.current
    if (!el) return
    const idle = () => {
      clearTimeout(timer.current)
      timer.current = setTimeout(() => setOn(false), idleMs)
    }
    const active = () => {
      const now = inside.current.hover || inside.current.focus
      setOn(now)
      if (now) idle()
    }
    const enter = () => {
      inside.current.hover = true
      active()
    }
    const leave = () => {
      inside.current.hover = false
      active()
    }
    const focusIn = () => {
      inside.current.focus = true
      active()
    }
    const focusOut = (e: FocusEvent) => {
      if (el.contains(e.relatedTarget as Node | null)) return
      inside.current.focus = false
      active()
    }
    el.addEventListener('pointerenter', enter)
    el.addEventListener('pointerleave', leave)
    el.addEventListener('pointermove', active)
    el.addEventListener('keydown', active)
    el.addEventListener('focusin', focusIn)
    el.addEventListener('focusout', focusOut)
    return () => {
      clearTimeout(timer.current)
      el.removeEventListener('pointerenter', enter)
      el.removeEventListener('pointerleave', leave)
      el.removeEventListener('pointermove', active)
      el.removeEventListener('keydown', active)
      el.removeEventListener('focusin', focusIn)
      el.removeEventListener('focusout', focusOut)
    }
  }, [root, idleMs])
  return on
}

/** The groups to render: live when idle, `stabilize`d against what is on screen while in use. */
export function useStableGroups<G, E>(
  groups: readonly G[],
  frozen: boolean,
  shape: StableShape<G, E>,
): G[] {
  const shown = useRef<G[] | undefined>(undefined)
  // biome-ignore lint/correctness/useExhaustiveDependencies: `shape` is a module constant
  return useMemo(() => {
    const next = frozen ? stabilize(shown.current, groups, shape) : [...groups]
    shown.current = next
    return next
  }, [groups, frozen])
}
